import resolveDevicePorts from '@Application/ApplicationSettings/DevicePorts/resolveDevicePorts';
import type { DevicePorts } from '@Domain/Ports/DevicePorts';
import OrphanedTransferNotifier from '@UI/Providers/PortsProvider/OrphanedTransferNotifier';
import RecordingTransferHost from '@UI/Providers/PortsProvider/RecordingTransferHost';
import OutboxRunnerHost from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/OutboxRunnerHost';
import PortsContext from '@UI/Providers/PortsProvider/PortsContext';
import * as React from 'react';

type Props = {
	readonly children: React.ReactNode;
	readonly ports?: Partial<DevicePorts>;
};

/**
 * `ports` lets a caller substitute any subset of the device ports with a fake, which is what makes
 * every screen that reads a port testable without mocking a single module.
 */
const PortsProvider: React.FC<Props> = ({ children, ports }) => {
	const defaultPorts = React.useMemo<DevicePorts>(() => resolveDevicePorts(), []);

	const value = React.useMemo<DevicePorts>(() => ({ ...defaultPorts, ...ports }), [defaultPorts, ports]);

	return (
		<PortsContext.Provider value={value}>
			{/*
				Mounted before `OutboxRunnerHost`, and this order is load-bearing: `registerHandler` for
				a `kind` must run before the engine can ever claim an item of that `kind`
				(`Domain/Ports/OutboxRunner/OutboxPort.ts`), and a `kind` still unhandled at claim time fails
				PERMANENTLY. Sibling effects run in mount order, so the registration effect below has to
				commit before `OutboxRunnerHost`'s own `start()` effect — putting this component under
				`UI/Application.tsx` instead would run it inside `{children}`, after the engine has
				already started. This couples an infrastructure provider to the helpers feature; that
				coupling is the price of an ordering guarantee only the ports' own composition root can
				hold (`guides/device-ports.md`).
			*/}
			<RecordingTransferHost />
			<OrphanedTransferNotifier />
			<OutboxRunnerHost />
			{children}
		</PortsContext.Provider>
	);
};

export default PortsProvider;
